<template>
    <el-input
        :id="elementName"
        v-model="value"
        type="search"
        class="span_12 h-4 algolia-input"
        :placeholder="placeholder"
        @change="change"
    />
</template>

<script>
import AddressFrenchDOMTOMAdapter from 'Services/Adapters/AddressFrenchDOMTOMAdapter.js';
import Feature from 'Services/Feature';

import JawgJSLoader from '@jawg/js-loader';
import Env from 'Services/Env';
import { mapState } from 'vuex';
import { formatAddressFromJawgs } from '../../helpers/addressHelpers';

export default {
    props: {
        defaultValue: {
            type: String,
            required: false,
            default: '',
        },
        placeType: {
            type: String,
            required: false,
            default: '',
        },
        elementName: {
            type: String,
            required: false,
            default: 'address-input',
        },
        templates: {
            type: [Object, Boolean],
            required: false,
            default: () => {
                return false;
            },
        },
        placeholder: {
            type: String,
            required: false,
            default: '',
        },
    },
    emits: ['bubble-address', 'algolia-disable'],
    data() {
        return {
            value: '',
            suggestion: {},
            placesAutocomplete: null,
            element: null,
            addressAdapter: new AddressFrenchDOMTOMAdapter(),
            token: Env.get('jawg').token,
            loader: null,
            location: {
                latitude: 46.1620299,
                longitude: -1.246513,
            },
            jawgLoader: null,
        };
    },
    computed: {
        ...mapState('corp', {
            corpMainAddress: 'mainAddress',
        }),
    },
    async mounted() {
        this.location.latitude = parseFloat(this.corpMainAddress?.lat) ?? this.location.latitude;
        this.location.longitude = parseFloat(this.corpMainAddress?.lng) ?? this.location.longitude;

        this.value = this.defaultValue;
        this.$nextTick(() => {
            this.element = document.getElementById(this.elementName);
            if (this.element) {
                this.element.value = this.defaultValue;
            }
        });
        if (!Feature.isEnabled('algolia')) {
            //algolia disabled
            this.$emit('algolia-disable', true);
            return;
        }
        const jawgPlaces = !window.JawgPlaces
            ? await this.jawgLoader.loadJawgPlaces()
            : await window.JawgPlaces;

        class FilteredInput extends jawgPlaces.Input {
            constructor(options) {
                super(options);
            }
            showResults(results) {
                const tempFeatures = [];
                for (const feature of results.features) {
                    const properties = feature.properties;
                    if (undefined === properties.postalcode || undefined === properties.locality) {
                        continue;
                    }
                    tempFeatures.push(feature);
                }
                results.features = tempFeatures;
                super.showResults(results);
            }
        }

        return new FilteredInput(this.buildOptionsJawgs());
    },
    created() {
        this.jawgLoader = new JawgJSLoader({ accessToken: this.token });
        EventBus.on('form:third:create:setAddressAlgolia', (payload) => {
            this.placesAutocomplete.setVal(payload);
        });
    },
    unmounted() {
        EventBus.off('form:third:create:setAddressAlgolia');
    },
    methods: {
        setVal(val) {
            this.placesAutocomplete.setVal(val);
        },
        change(value) {
            //No autocomp
            if (value != this.suggestion.value) {
                this.$emit('bubble-address', { value, type: 'noAlgolia', id: this.elementName });
            }
        },
        failback() {
            this.placesAutocomplete.destroy();
            this.element.focus();
            this.$emit('algolia-disable', true);
        },
        buildOptionsJawgs() {
            return {
                input: `#${this.elementName}`,
                language: 'fr',
                searchOnTyping: true,
                layers: ['address', 'venue', 'locality'],
                focusPoint: {
                    lat: this.location.latitude,
                    lon: this.location.longitude,
                },
                onClick: (feature) => {
                    const address = formatAddressFromJawgs(feature);
                    this.addressAdapter.set(address).adapt();
                    this.$emit('bubble-address', address);
                },
                onError: () => this.failback(),
            };
        },
    },
};
</script>

<style scoped lang="scss">
.algolia-input {
    z-index: 1;
    :deep(.jawg-places-container) {
        width: 100%;
    }
}
</style>
